import queryString from 'query-string';
import {
  FORCE_ERROR_FALLBACK,
  FORCE_SUCCESS_MESSAGE,
  FORCEABLE_PRODUCT_CODES,
} from '../constants/forceGuarantee';

// Mappers from the raw GraphQL payloads to the shapes the reducer stores and the views render.

export const toTransaction = order => ({ ...order, id: order.orderId });

export const toRuleOutcome = (outcome, index) => ({
  id: `${outcome.ruleName}-${index}`,
  ruleName: outcome.ruleName,
  outcome: outcome.passed ? 'pass' : 'fail',
  reason: outcome.reason,
});

// Transport/GraphQL failures: prefer the message the backend sent over the fallback.
export const toErrorMessage = (error, fallback) => {
  if (!error) return null;
  const graphQLErrors = error.graphQLErrors || [];
  return (graphQLErrors[0] && graphQLErrors[0].message) || fallback;
};

// APMS failures come back as `success: false` with a generic message in `errors`.
export const toForceResult = payload => {
  const result = payload || {};
  const errors = result.errors || [];
  return {
    success: !!result.success,
    status: result.forceGuaranteeResult
      ? result.forceGuaranteeResult.status
      : null,
    message: result.success
      ? FORCE_SUCCESS_MESSAGE
      : errors.join(' ') || FORCE_ERROR_FALLBACK,
  };
};

// --- Row-context launch: the context travels in the query string and carries `externalId` ---

// A shallow check only: APMS remains the authority on VIN validity.
const VIN_MAX_LENGTH = 17;

// A repeated parameter parses as an array; take the first rather than comma-joining them.
const firstValue = value => (Array.isArray(value) ? value[0] : value);

const cleanString = value => {
  const raw = firstValue(value);
  if (raw === null || raw === undefined) return null;
  const trimmed = String(raw).trim();
  return trimmed === '' ? null : trimmed;
};

const cleanVin = value => {
  const vin = cleanString(value);
  if (!vin) return null;
  if (/\s/.test(vin)) return null;
  if (vin.length > VIN_MAX_LENGTH) return null;
  return vin.toUpperCase();
};

// Blank fields are omitted rather than sent empty as a `?vin=` the parser has to reject.
export const buildForceGuaranteeSearch = ({ productCode, vin, externalId } = {}) => {
  const context = {
    productCode: cleanString(productCode),
    vin: cleanString(vin),
    externalId: cleanString(externalId),
  };
  const query = queryString.stringify(
    Object.keys(context).reduce((acc, key) => (context[key] ? { ...acc, [key]: context[key] } : acc), {})
  );
  return query ? `?${query}` : '';
};

// Validated per field: a junk `externalId` still allows the lookup and just pre-selects nothing.
export const parseForceGuaranteeContext = search => {
  const params = queryString.parse(search || '');
  const productCode = cleanString(params.productCode);
  return {
    productCode: FORCEABLE_PRODUCT_CODES.includes(productCode) ? productCode : null,
    vin: cleanVin(params.vin),
    externalId: cleanString(params.externalId),
  };
};

// `ruleEvaluationOrders` is the only endpoint exposing both identifiers, so it does the translation.
export const findOrderIdByExternalId = (orders, externalId) => {
  const wanted = cleanString(externalId);
  if (!wanted || !orders || !orders.length) return null;
  const match = orders.find(order => order && cleanString(order.externalId) === wanted);
  return match && match.orderId !== null && match.orderId !== undefined
    ? match.orderId
    : null;
};

// Compared as strings: the same identifier is an ID in one API and an Integer in the other.
export const isSameOrderId = (a, b) => {
  const left = cleanString(a);
  const right = cleanString(b);
  return !!left && !!right && left === right;
};
